import React, { useState, useEffect, useCallback } from 'react';
import Modal, { ModalCloseButton } from './Modal';
import ConfirmDialog from './ConfirmDialog';
import { apiFetch } from '../api';
import { parseTermin, terminDayLabel, terminTime, terminDayKey, notifyTermineChanged } from '../terminUtils';
import { IconTrash } from './icons';

const TERMINE_API = '/api/termine';

// Termine-Tab: Agenda-Ansicht, nach Tagen gruppiert (user-friendly statt Kalendergitter)
export default function TermineModal({ onClose, authHeader, currentUser }) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [showPast, setShowPast] = useState(false);
  // Termin, dessen Löschen noch bestätigt werden muss
  const [pendingRemove, setPendingRemove] = useState(null);

  const isAdmin = currentUser?.role === 'admin';

  const reload = useCallback(() => {
    setLoadError(false);
    apiFetch(TERMINE_API, { headers: authHeader })
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(setData)
      .catch(() => setLoadError(true));
  }, [authHeader]);

  useEffect(() => { reload(); }, [reload]);

  const toggleDone = async t => {
    try {
      const res = await apiFetch(`${TERMINE_API}/${t.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({ erledigt: t.erledigt ? 0 : 1 }),
      });
      if (res.ok) {
        const u = await res.json();
        setData(prev => prev.map(x => x.id === t.id ? { ...x, erledigt: u.erledigt } : x));
        notifyTermineChanged();
      }
    } catch {}
  };

  const remove = async id => {
    try {
      const res = await apiFetch(`${TERMINE_API}/${id}`, { method: 'DELETE', headers: authHeader });
      if (res.ok) {
        setData(prev => prev.filter(x => x.id !== id));
        notifyTermineChanged();
      }
    } catch {}
  };

  // Nach Tagen gruppieren; vergangene optional ausblenden
  const todayKey = terminDayKey(new Date());
  const groups = [];
  if (data) {
    const visible = data
      .map(t => ({ ...t, _d: parseTermin(t.termin_at) }))
      .filter(t => t._d && (showPast || terminDayKey(t._d) >= todayKey))
      .sort((a, b) => a._d - b._d);
    const byDay = new Map();
    for (const t of visible) {
      const k = terminDayKey(t._d);
      if (!byDay.has(k)) byDay.set(k, []);
      byDay.get(k).push(t);
    }
    for (const [k, items] of byDay) groups.push({ key: k, label: terminDayLabel(new Date(k)), items });
  }

  return (
    <Modal onClose={onClose} label="Termine">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2>Termine</h2>
        <ModalCloseButton onClose={onClose} />
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#6b7280', marginBottom: 16, cursor: 'pointer' }}>
        <input type="checkbox" checked={showPast} onChange={e => setShowPast(e.target.checked)} />
        Vergangene Termine anzeigen
      </label>

      {!data && !loadError && <p style={{ textAlign: 'center', color: '#6b7280', padding: '24px 0' }}>Lädt...</p>}
      {loadError && <div className="login-error" role="alert" style={{ textAlign: 'center' }}>Daten konnten nicht geladen werden.</div>}
      {data && groups.length === 0 && (
        <p style={{ textAlign: 'center', color: '#6b7280', padding: '32px 0', fontSize: 14 }}>
          Keine {showPast ? '' : 'anstehenden '}Termine. Termine legst du in der Lead-Detailansicht an.
        </p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {groups.map(g => (
          <div key={g.key}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#5b21b6', textTransform: 'capitalize', marginBottom: 8 }}>{g.label}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {g.items.map(t => {
                const addr = [t.strasse, t.hausnummer, t.zusatz].filter(Boolean).join(' ');
                const fullAddr = [addr, t.ort].filter(Boolean).join(', ');
                return (
                  <div key={t.id} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 10,
                    background: t.erledigt ? '#f3f4f6' : '#f9f8ff', border: '1px solid #ede9fe',
                  }}>
                    <span style={{ flexShrink: 0, fontWeight: 800, fontSize: 15, color: '#5b21b6', minWidth: 46 }}>
                      {terminTime(t._d)}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontWeight: 700, fontSize: 14, color: '#1e1b4b',
                        textDecoration: t.erledigt ? 'line-through' : 'none',
                      }}>
                        {t.titel || 'Termin'}
                      </div>
                      {fullAddr && <div style={{ fontSize: 12, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fullAddr}</div>}
                      {isAdmin && <div style={{ fontSize: 11, color: '#9ca3af' }}>{t.username}</div>}
                    </div>
                    <button
                      type="button" onClick={() => toggleDone(t)}
                      aria-label={t.erledigt ? 'Als offen markieren' : 'Als erledigt markieren'}
                      style={{
                        flexShrink: 0, width: 28, height: 28, borderRadius: '50%', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: `2px solid ${t.erledigt ? '#16a34a' : '#d1d5db'}`,
                        background: t.erledigt ? '#16a34a' : 'white', color: 'white',
                      }}
                    >
                      {t.erledigt && (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
                      )}
                    </button>
                    <button
                      type="button" onClick={() => setPendingRemove(t)} aria-label="Termin löschen"
                      style={{ flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 6, lineHeight: 0 }}
                    >
                      <IconTrash />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {pendingRemove && (
        <ConfirmDialog
          title="Termin löschen?"
          message={`„${pendingRemove.titel || 'Termin'}“ am ${pendingRemove._d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })} um ${terminTime(pendingRemove._d)} Uhr wird gelöscht.`}
          onConfirm={() => { remove(pendingRemove.id); setPendingRemove(null); }}
          onCancel={() => setPendingRemove(null)}
        />
      )}
    </Modal>
  );
}
